/**
 * Image compression utility
 * Compresses images to reduce file size while maintaining acceptable quality
 */

interface CompressionOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  useWebWorker?: boolean;
  quality?: number;
}

/**
 * Compresses an image file using browser native Canvas API
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Compressed File object
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<File> {
  const {
    maxSizeMB = 2, // Target max file size in MB
    maxWidthOrHeight = 1920, // Max dimension (width or height)
    quality = 0.85, // JPEG quality (0-1)
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidthOrHeight || height > maxWidthOrHeight) {
          if (width > height) {
            height = (height * maxWidthOrHeight) / width;
            width = maxWidthOrHeight;
          } else {
            width = (width * maxWidthOrHeight) / height;
            height = maxWidthOrHeight;
          }
        }
        
        // Create canvas and compress
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob with compression
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            
            // Check if we need further compression
            const sizeMB = blob.size / (1024 * 1024);
            
            if (sizeMB > maxSizeMB) {
              // Recursively compress more if still too large
              const newQuality = Math.max(0.3, quality - 0.1);
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              
              compressImage(compressedFile, {
                maxSizeMB,
                maxWidthOrHeight,
                quality: newQuality,
              })
                .then(resolve)
                .catch(reject);
            } else {
              // Create new File with compressed blob
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              
              console.log(
                `✅ Image compressed: ${(file.size / (1024 * 1024)).toFixed(2)}MB → ${(compressedFile.size / (1024 * 1024)).toFixed(2)}MB`
              );
              
              resolve(compressedFile);
            }
          },
          'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = e.target?.result as string;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
}

/**
 * Compresses multiple image files
 * @param files - Array of image files to compress
 * @param options - Compression options
 * @returns Promise with array of compressed files
 */
export async function compressImages(
  files: File[],
  options: CompressionOptions = {}
): Promise<File[]> {
  try {
    const compressedFiles = await Promise.all(
      files.map((file) => compressImage(file, options))
    );
    return compressedFiles;
  } catch (error) {
    console.error('❌ Error compressing images:', error);
    // Fallback: return original files if compression fails
    return files;
  }
}

/**
 * Compresses a base64 image string
 * @param base64String - Base64 image string (with or without data:image prefix)
 * @param options - Compression options
 * @returns Promise with compressed base64 string
 */
export async function compressBase64Image(
  base64String: string,
  options: CompressionOptions = {}
): Promise<string> {
  try {
    // Remove data URL prefix if present
    const base64Data = base64String.includes(',') 
      ? base64String.split(',')[1] 
      : base64String;
    
    // Convert base64 to blob
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/jpeg' });
    
    // Create File from blob
    const file = new File([blob], 'camera-capture.jpg', {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
    
    // Compress the file
    const compressedFile = await compressImage(file, options);
    
    // Convert compressed file back to base64
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result); // Returns full data URL with prefix
      };
      reader.onerror = reject;
      reader.readAsDataURL(compressedFile);
    });
  } catch (error) {
    console.error('❌ Error compressing base64 image:', error);
    // Fallback: return original base64 string
    return base64String;
  }
}

